/**
 * Wikilink extension factory for the 3mote MarkdownEditor (Wave C3).
 *
 * Notion-style `[[wikilink]]` inline references. The user types `[[`
 * inside the editor; a picker opens showing every tenant doc + folder
 * (empty-query fallback sources from `useWikiTree().nodesById`) and
 * filters by substring as the user types; selecting one inserts a chip
 * referencing that node by stable id. (`/` is reserved for a future
 * block-level slash menu — wikilinks stay on the `[[` Obsidian
 * convention.)
 *
 * On-the-wire markdown shape (canonical per the wave-C3 brief):
 *
 *   [[nodeId|nodeName]]
 *
 * The pipe-separated form is deliberate. Node ids alone aren't
 * human-readable (raw UUIDs in a markdown export are useless for AI
 * agents reading the file), and node names alone aren't stable (a
 * rename downstream of an export would break every reference). The
 * `|name` half is the displayed label cached at insert time; the
 * `nodeId` half is the durable reference that survives renames.
 *
 * Round-trip strategy:
 *
 *   1. **Serialize.** Override the wikilink node's
 *      `storage.markdown.serialize` to emit `[[id|name]]` verbatim
 *      (with `|` / `]` / `\` characters in the name escaped as
 *      `\|` / `\]` / `\\` so the bracket-pair parser stays simple).
 *
 *   2. **Parse.** Register a custom markdown-it inline rule via
 *      `parse.setup` that tokenizes `[[id|name]]` and emits a
 *      `<span data-type="wikilink" data-id="..." data-label="...">`.
 *      The extension's `parseHTML` matches that span and hydrates a
 *      wikilink node back into the document.
 *
 * Trigger detection (two-character `[[`):
 *
 *   `@tiptap/suggestion`'s `char` option is a single character. We
 *   register the suggestion with `char: '['` and an `allow` predicate
 *   that requires the position just before the trigger `[` to also be
 *   `[`. Anything else (a literal `[link](url)`, a single `[` in
 *   isolation, or an escaped `\[`) is ignored. This avoids forking
 *   the upstream `findSuggestionMatch` while still giving the
 *   user-facing "type `[[` to insert" behavior.
 *
 * Why extend `Mention` instead of `Node`?
 *
 *   The wikilink shares ProseMirror node shape with mentions (inline
 *   atom, selectable, single id-keyed payload, `suggestion` plugin
 *   for popover-driven insert). Extending `Mention` reuses the wiring
 *   `@tiptap/extension-mention` already ships (`addOptions`,
 *   `addProseMirrorPlugins` → `Suggestion(...)`, the
 *   `deleteTriggerWithBackspace` ergonomic) and keeps the FE's
 *   transitive-dep boundary intact — `3mote-fe-new/package.json` has
 *   `@tiptap/extension-mention` as a direct dep but NOT
 *   `@tiptap/suggestion` / `@tiptap/core`, and reaching for those
 *   directly would break the dep contract. Same pattern as
 *   `src/features/chat/lib/channel-mention-extension.ts`.
 *
 *   The trade-off: Mention's default options store `nodeId` + `label`
 *   under attribute names `id` + `label` (not `nodeId` + `nodeName`).
 *   We override the attribute names below to match the wikilink
 *   contract, and the chip renders + the markdown serializer read
 *   from the new names. The on-wire markdown shape stays
 *   `[[id|name]]` — the attribute-name change is internal-only.
 *
 * Chip rendering:
 *
 *   `.wikilink-chip` in `src/styles/global.css`. When the resolved
 *   node has been soft-deleted (consumer-side check against
 *   `useWikiTree().nodesById`), the consumer applies a
 *   `data-broken="true"` attribute and CSS surfaces the broken-link
 *   treatment. The serialization shape is unchanged — the markdown
 *   still emits `[[id|name]]` verbatim, so if the page is restored
 *   (P3 feature), the chip auto-resolves on next render.
 */

import Mention, { type MentionOptions } from '@tiptap/extension-mention'
import type { Node as ProseMirrorNode } from '@tiptap/pm/model'
import { ReactNodeViewRenderer } from '@tiptap/react'

import { WikiLinkNodeView } from './wikilink-nodeview'

/**
 * Markdown-it inline-state shape we actually touch. Limiting to the
 * surface we need (rather than importing `markdown-it`'s `StateInline`
 * type, which isn't a direct dep here) keeps the type boundary local
 * and the runtime behavior obvious.
 */
interface MarkdownItInlineState {
  src: string
  pos: number
  posMax: number
  push: (type: string, tag: string, nesting: number) => MarkdownItToken
}

interface MarkdownItToken {
  meta?: Record<string, unknown> | null
  attrSet?: (name: string, value: string) => void
  attrGet?: (name: string) => string | null
  content?: string
}

interface MarkdownItForSetup {
  inline: {
    ruler: {
      before: (target: string, name: string, fn: (state: MarkdownItInlineState) => boolean) => void
    }
  }
  renderer: {
    rules: Record<
      string,
      (
        tokens: Array<MarkdownItToken>,
        idx: number,
        opts: unknown,
        env: unknown,
        self: unknown,
      ) => string
    >
  }
  utils: {
    escapeHtml: (s: string) => string
  }
}

/**
 * Serializer for the wikilink node. Emits `[[id|name]]` verbatim.
 *
 * Escaping rationale:
 *   - `|` inside the displayed name is escaped to `\|` so the
 *     splitter on parse stays simple (`name` ends at the first
 *     unescaped pipe).
 *   - `]` inside the displayed name is escaped to `\]` so a name
 *     containing a literal `]` doesn't terminate the wikilink early.
 *   - `\` is escaped to `\\` so the escape mechanism itself
 *     round-trips.
 *   - Node ids are UUIDs in practice — no escaping needed — but we
 *     defensively backslash any `|` / `]` / `\` characters in case
 *     a future id format introduces non-UUID characters.
 */
function serializeWikilink(state: { write: (s: string) => void }, node: ProseMirrorNode) {
  const id = String(node.attrs.nodeId ?? '')
  const name = String(node.attrs.nodeName ?? id)
  const safeId = id.replace(/[|\]\\]/g, (m) => `\\${m}`)
  const safeName = name.replace(/[|\]\\]/g, (m) => `\\${m}`)
  state.write(`[[${safeId}|${safeName}]]`)
}

/**
 * Register the wikilink inline rule on the markdown-it instance
 * tiptap-markdown owns. Recognizes `[[id|name]]`, honors the small
 * set of escape characters, and emits a `wikilink_inline` token. The
 * companion renderer below converts the token into a span the
 * extension's `parseHTML` matches.
 *
 * Registered BEFORE the `link` rule so a single-`[` markdown link is
 * unaffected — `[[uuid|name]]` is consumed as a wikilink, while a
 * plain `[label](url)` later falls through to the canonical link
 * rule.
 *
 * Escape behavior: a literal `\[\[not-a-link\]\]` outside of a
 * wikilink doesn't match this rule. markdown-it's standard escape
 * rule consumes each `\[` / `\]` pair before our rule runs, so the
 * "escaped brackets stay as escaped brackets" test case naturally
 * holds.
 */
function setupWikilinkMarkdownIt(md: MarkdownItForSetup) {
  const wikilinkRule = (state: MarkdownItInlineState): boolean => {
    const start = state.pos
    if (state.src.charCodeAt(start) !== 0x5b /* [ */) return false
    if (state.src.charCodeAt(start + 1) !== 0x5b /* [ */) return false

    // Walk forward looking for the closing `]]`, honoring escaped
    // characters. Cap at posMax (markdown-it's per-block limit).
    let i = start + 2
    let pipeAt = -1
    let foundClose = -1
    while (i < state.posMax - 1) {
      const ch = state.src.charCodeAt(i)
      if (ch === 0x5c /* \ */) {
        // Escaped next char — skip it.
        i += 2
        continue
      }
      if (ch === 0x7c /* | */ && pipeAt < 0) {
        pipeAt = i
      }
      if (ch === 0x5d /* ] */ && state.src.charCodeAt(i + 1) === 0x5d) {
        foundClose = i
        break
      }
      if (ch === 0x0a /* newline */) {
        // Wikilinks don't span lines.
        return false
      }
      i += 1
    }
    if (foundClose < 0 || pipeAt < 0 || pipeAt >= foundClose) return false

    const rawId = state.src.slice(start + 2, pipeAt)
    const rawName = state.src.slice(pipeAt + 1, foundClose)
    // Unescape the small set of characters the serializer escaped.
    // (Renamed from `unescape` to avoid shadowing the deprecated
    // global of the same name — Biome's `noShadowRestrictedNames`.)
    const unescapeWikilinkChar = (s: string) => s.replace(/\\([|\]\\])/g, '$1')
    const id = unescapeWikilinkChar(rawId).trim()
    const name = unescapeWikilinkChar(rawName).trim()
    if (id.length === 0 || name.length === 0) return false

    const token = state.push('wikilink_inline', '', 0)
    token.attrSet?.('data-type', 'wikilink')
    token.attrSet?.('data-id', id)
    token.attrSet?.('data-label', name)
    token.content = name

    state.pos = foundClose + 2
    return true
  }

  md.inline.ruler.before('link', 'wikilink', wikilinkRule)
  md.renderer.rules.wikilink_inline = (tokens, idx) => {
    const token = tokens[idx]
    if (!token) return ''
    const id = token.attrGet?.('data-id') ?? ''
    const label = token.attrGet?.('data-label') ?? token.content ?? ''
    const esc = md.utils.escapeHtml
    return `<span data-type="wikilink" data-id="${esc(id)}" data-label="${esc(label)}">${esc(label)}</span>`
  }
}

export interface BuildWikiLinkExtensionOptions {
  /**
   * Optional `suggestion` config — when set, typing `[[` opens the
   * picker. Omit on read-only surfaces (rendered markdown without an
   * editor) and on call-sites that don't want wikilink authoring (the
   * round-trip spike test uses this default; chat / comments / card
   * descriptions also omit it for now).
   */
  suggestion?: MentionOptions['suggestion']
}

/**
 * Build the configured WikiLink TipTap node. Returns a fresh extension
 * per call — same constraint as the mention extension (TipTap doesn't
 * like sharing extension instances across editors).
 *
 * Extends `Mention` rather than building a bare `Node` so we reuse the
 * underlying `Suggestion(...)` wiring without reaching directly into
 * `@tiptap/suggestion` (which is a transitive dep, not a direct one —
 * see the module-level comment). The `name: 'wikilink'` override is
 * what keeps the wikilink node distinct from `@user` mentions and
 * `#channel` mentions in ProseMirror's schema (the same critical
 * pitfall called out in `channel-mention-extension.ts`'s docstring —
 * each mention-extended node MUST have a unique name).
 */
export function buildWikiLinkExtension({ suggestion }: BuildWikiLinkExtensionOptions = {}) {
  return Mention.extend({
    name: 'wikilink',

    /**
     * Attribute shape — `nodeId` + `nodeName` instead of the
     * Mention-default `id` + `label`. Drives both the markdown
     * serializer and the chip render below; the on-wire markdown
     * stays `[[id|name]]` so the attribute-name change is purely
     * internal.
     */
    addAttributes() {
      return {
        nodeId: {
          default: null,
          parseHTML: (element: HTMLElement) => element.getAttribute('data-id'),
          renderHTML: (attrs: Record<string, unknown>) => {
            if (!attrs.nodeId) return {}
            return { 'data-id': String(attrs.nodeId) }
          },
        },
        nodeName: {
          default: null,
          parseHTML: (element: HTMLElement) => element.getAttribute('data-label'),
          renderHTML: (attrs: Record<string, unknown>) => {
            if (!attrs.nodeName) return {}
            return { 'data-label': String(attrs.nodeName) }
          },
        },
      }
    },

    parseHTML() {
      // Match both shapes: the newer `<a>` chips (renderHTML
      // emits anchors now) AND legacy `<span>` chips from any doc
      // inserted before this change. Markdown-it's inline rule emits
      // its own shape too — the markdown parser hydrates via
      // `addStorage().markdown.parse.setup` below, which converts
      // `[[id|name]]` tokens into the wikilink node directly.
      return [{ tag: 'a[data-type="wikilink"]' }, { tag: 'span[data-type="wikilink"]' }]
    },

    addStorage() {
      return {
        markdown: {
          serialize: serializeWikilink,
          parse: {
            setup: setupWikilinkMarkdownIt,
          },
        },
      }
    },

    // React NodeView. Live in-editor render goes through this; the
    // static `renderHTML` above still applies to paste / SSR / HTML
    // export paths. The NodeView resolves `data-broken` against the
    // wiki-tree cache at render time so a soft-deleted target shows
    // the broken-link CSS treatment immediately (the static path
    // can't read hook context, so it never sets `data-broken`).
    addNodeView() {
      return ReactNodeViewRenderer(WikiLinkNodeView)
    },
  }).configure({
    HTMLAttributes: {
      class: 'wikilink-chip',
      'data-type': 'wikilink',
    },

    /**
     * Plain-text representation when the node is selected + copied.
     * Use the displayed label so a paste into a non-wikilink-aware
     * surface (Slack, plain textarea) reads as the page name rather
     * than the opaque UUID.
     */
    renderText({ node }) {
      return String(node.attrs.nodeName ?? node.attrs.nodeId ?? '')
    },

    /**
     * Render the chip. The Mention extension's default `renderHTML`
     * uses the node's renderText for the chip body; we override to
     * make `data-id` + `data-label` first-class on the rendered
     * element (consumers depending on the chip's data attrs — e.g.
     * the broken-link `data-broken="true"` CSS hook — can read them
     * directly off the DOM without walking node attrs).
     */
    renderHTML({ options, node }) {
      const id = String(node.attrs.nodeId ?? '')
      const label = String(node.attrs.nodeName ?? id)
      // Render as `<a target="_blank">` so the browser handles every
      // click natively — plain click, Cmd / Ctrl / middle / right-click
      // all open the linked doc in a new tab. This sidesteps the
      // editor's contentEditable + ProseMirror selection-on-click flow,
      // the unsaved-changes nav-guard race when clicking a link mid-edit,
      // and any need for a delegated JS click handler. Matches Notion /
      // Obsidian wikilink behavior. `noopener noreferrer` is required
      // security hygiene for any `target="_blank"`.
      return [
        'a',
        {
          ...options.HTMLAttributes,
          href: `/wiki/d/${id}`,
          target: '_blank',
          rel: 'noopener noreferrer',
          'data-id': id,
          'data-label': label,
        },
        label,
      ]
    },

    deleteTriggerWithBackspace: true,

    /**
     * Suggestion config — `char: '['` plus an `allow` predicate that
     * requires the character immediately before the trigger to ALSO
     * be `[`. The user must type `[[` to open the picker; a single
     * `[` (start of a normal markdown link) leaves the suggestion
     * dormant.
     *
     * The default Mention suggestion strips the trigger char on
     * commit; for the two-char `[[` opener that leaves a stray `[`
     * behind. We override `command` (when callers supply their own
     * suggestion config we merge in the override; when no suggestion
     * is supplied we fall through to the trivial default that does
     * nothing — same as the existing mention extension's default).
     */
    // The Mention extension's `suggestion` slot is typed against
    // `MentionNodeAttrs` (id + label). Our wikilink picker commits
    // the same shape (id + label — see `WikiLinkSuggestionItem`), so
    // the runtime contract is identical; the cast collapses the
    // structurally-equivalent generic mismatch.
    //
    // The `allow` + `command` overrides handle the two-char `[[`
    // trigger ergonomics: `allow` rejects suggestion-open events unless
    // the character before the `[` trigger is also `[`, and `command`
    // expands the replacement range one char to the left so both
    // opening `[` characters are consumed when the chip is inserted.
    // (`/` is reserved for a future block-level slash menu — wikilinks
    // stay on `[[` for the Notion / Obsidian convention.)
    suggestion: (suggestion
      ? {
          ...suggestion,
          char: suggestion.char ?? '[',
          allowedPrefixes: null,
          allow: ((props: {
            editor: unknown
            state: {
              selection: { $from: { nodeBefore: { isText?: boolean; text?: string } | null } }
            }
            range: { from: number; to: number }
            isActive?: boolean
          }) => {
            const callerAllow = (suggestion as { allow?: (p: unknown) => boolean }).allow
            if (typeof callerAllow === 'function' && !callerAllow(props)) return false
            const $from = props.state.selection.$from
            const textBefore = $from.nodeBefore?.isText ? ($from.nodeBefore.text ?? '') : ''
            // text-before-caret ends with the trigger `[`; the second-to-last
            // character is what we gate on. `[[`-typed → second-to-last is `[` → allow.
            const triggerAt = textBefore.length - 1
            if (triggerAt < 1) return false
            return textBefore.charAt(triggerAt - 1) === '['
          }) as MentionOptions['suggestion']['allow'],
          command: (({
            editor,
            range,
            props,
          }: {
            editor: {
              chain: () => {
                focus: () => {
                  insertContentAt: (
                    range: { from: number; to: number },
                    content: unknown,
                  ) => { run: () => unknown }
                }
              }
            }
            range: { from: number; to: number }
            props: { id: string; label: string }
          }) => {
            const expandedRange = { from: Math.max(0, range.from - 1), to: range.to }
            editor
              .chain()
              .focus()
              .insertContentAt(expandedRange, [
                {
                  type: 'wikilink',
                  attrs: { nodeId: props.id, nodeName: props.label },
                },
                { type: 'text', text: ' ' },
              ])
              .run()
          }) as MentionOptions['suggestion']['command'],
        }
      : { char: '[' }) as MentionOptions['suggestion'],
  })
}
